<div class="drawer">
  <header class="drawer-header">
    <span class="drawer-title">{{ configs[currentMode].title }}</span>
    <button type="button" class="drawer-close" (click)="cancel()" aria-label="Close">×</button>
  </header>

  <div class="drawer-body">
    <div class="section-header">General</div>
    <div class="row two-col">
      <app-titled-input [control]="name" [title]="'Brand name'">
        <ng-content>
          <input type="text" [formControl]="name" (blur)="onNameBlur()">
        </ng-content>
      </app-titled-input>
      <app-titled-input [control]="brandKey" [title]="'Brand key'">
        <ng-content>
          <input type="text" [formControl]="brandKey">
        </ng-content>
        <div inputNote class="input-note" *ngIf="suggestedKey()">
          The brand name was changed.
          <button type="button" class="input-note-action" (click)="refreshKey()">Click here</button>
          to refresh the brand key
        </div>
      </app-titled-input>
    </div>

    <div class="select-menu-input-wrapper">
      <app-titled-input [control]="competingBrands" [title]="'Competitors'"></app-titled-input>
      <app-select-menu [items]="competitorOptions()" [placeholder]="'Select brand'" [searchable]="true"
        [virtualScroll]="true" [control]="competingBrands" [selectMode]="SelectMode.MULTI" [selectAllAndClearOptions]="true"
        appendTo="body" dropDownPosition="auto"></app-select-menu>
    </div>

    <div class="logo-container">
      <div class="logo">
        <app-loader *ngIf="logoLoading"></app-loader>
        <img [src]="logoPath.value" (error)="onLogoError($event)">
      </div>
      <div class="controls">
        <div class="buttons">
          <app-button class="find" [class]="!name.value || logoLoading ? 'disabled' : 'primary'"
            text="Find automatically" (clicked)="findLogo()"></app-button>
          <app-button class="upload" [class]="!name.value || logoLoading ? 'disabled' : 'secondary'"
            text="Upload a logo" (clicked)="uploadLogo()"></app-button>
        </div>
        <span class="note">* .jpg or .png file up to 2MB. Image should be square.</span>
        <span class="message" [class]="logoMessageState.class">{{ logoMessageState.message }}</span>
      </div>
    </div>

    <div class="section-header">Open-Web</div>
    <div class="search-domains-container">
      <div class="title-and-copy-container">
        Advertiser domains
        <button (click)="copyAdvertiserDomains()" class="copy-button"><img [src]="'svg/copy.svg' | assetVersion"></button>
      </div>
      <app-form-chip-list [chips]="selectedResults" [control]="advertiserDomains"></app-form-chip-list>
      <div class="search-bar-container">
        <div class="search-bar">
          <app-search-bar (onSubmitSearch)="search()" placeholder="Search advertiser domains..."
            [searchControl]="searchControl"></app-search-bar>
        </div>
        <div class="search-button">
          <app-button text="Search" [class]="getValidSearch() ? 'primary' : 'primary disabled'" (clicked)="search()"></app-button>
        </div>
      </div>
    </div>
    <app-search-result-list [results]="unselectedResults" [placeholder]='resultPlaceholder'
      [resultClickCallback]="redirectCallback" [isLoading]="isSearching"></app-search-result-list>
    <div class="add-domain-container">
      <app-titled-input [control]="manualAdvertiserDomain">
        <ng-content>
          <input type="text" class="add-domain-input" (keydown.enter)="$event.preventDefault(); addManualAdvertiserDomain()" [formControl]="manualAdvertiserDomain"
            placeholder="Type advertiser domain to add it manually">
        </ng-content>
      </app-titled-input>
      <app-button text="Add manual advertiser domain" [class]="'secondary'" (clicked)="addManualAdvertiserDomain()"></app-button>
    </div>

    <div class="match-terms-container">
      <div class="match-terms-title">Click URL Terms
        <app-tooltip-icon [text]="tooltips.clickUrlTerms" [wide]="true"></app-tooltip-icon>
      </div>
      <app-form-chip-list [chips]="selectedClickUrlTerms" [control]="clickUrlTerms"></app-form-chip-list>
      <div class="add-domain-container">
        <app-titled-input [control]="manualClickUrlTerm">
          <ng-content>
            <input type="text" class="add-domain-input" (keydown.enter)="$event.preventDefault(); addManualClickUrlTerm()" [formControl]="manualClickUrlTerm"
              placeholder="Type click URL term to add it manually">
          </ng-content>
        </app-titled-input>
        <app-button text="Add click URL term" [class]="manualClickUrlTerm.invalid ? 'secondary disabled' : 'secondary'"
          (clicked)="addManualClickUrlTerm()"></app-button>
      </div>
    </div>

    <div class="match-terms-container">
      <div class="match-terms-title">Creative Text (OCR) Terms
        <span class="experimental-badge">Experimental</span>
        <app-tooltip-icon [text]="tooltips.ocrTerms" [wide]="true"></app-tooltip-icon>
      </div>
      <!-- UI intentionally inert while the feature is evaluated; existing terms still show and are preserved on save. -->
      <div class="match-terms-disabled">
        <app-form-chip-list [chips]="selectedOcrTerms" [control]="ocrTerms"></app-form-chip-list>
        <div class="add-domain-container">
          <app-titled-input [control]="manualOcrTerm">
            <ng-content>
              <input type="text" class="add-domain-input" (keydown.enter)="$event.preventDefault(); addManualOcrTerm()" [formControl]="manualOcrTerm"
                placeholder="Type creative text (OCR) term to add it manually">
            </ng-content>
          </app-titled-input>
          <app-button text="Add OCR term" [class]="'secondary'" (clicked)="addManualOcrTerm()"></app-button>
        </div>
      </div>
    </div>

    <div class="section-header">Social</div>
    <app-meta-pages [array]="metaPages" [existingBrandKey]="entity?.brandKey ?? null"></app-meta-pages>
    <app-linkedin-terms [array]="linkedinTerms" [existingBrandKey]="entity?.brandKey ?? null"></app-linkedin-terms>

    <!-- Edit mode only: a brand must exist before it can be researched. Trigger eligibility
         (does the brand have a researchable source?) is the server's call. -->
    <ng-container *ngIf="entity?.brandKey as existingBrandKey">
      <div class="section-header">Research</div>
      <app-research-section [brandKey]="existingBrandKey" [brandName]="entity?.name ?? existingBrandKey"></app-research-section>
    </ng-container>

    <div class="section-header">Organic</div>
    <app-titled-input [control]="keywords" [title]="'Keywords for brand detection in content (separated with comma)'">
      <app-tooltip-icon [text]="tooltips.keywords"></app-tooltip-icon>
      <ng-content>
        <textarea type="text" [formControl]="keywords"></textarea>
      </ng-content>
    </app-titled-input>
    <div class="section-header">AI Presence</div>
    <div class="match-terms-container">
      <div class="match-terms-title">AI Presence enabled
        <app-tooltip-icon [text]="tooltips.aiPresenceEnabled" [wide]="true"></app-tooltip-icon>
        <app-toggle-switch [enabled]="!!aiPresenceEnabled.value" (toggled)="setAiPresenceEnabled($event)"></app-toggle-switch>
      </div>
      <span class="ai-presence-note" *ngIf="aiPresenceEnabled.value && !aiPresenceTopics.value?.length">
        No topics attached. Collection runs only for topics inherited from brands that list this brand as a competitor.
      </span>
    </div>

    <div class="select-menu-input-wrapper">
      <app-titled-input [control]="aiPresenceTopics" [title]="'AI Presence Topics'"></app-titled-input>
      <app-select-menu [items]="topicOptions()" [searchable]="true" [control]="aiPresenceTopics"
        [selectMode]="SelectMode.MULTI" [selectAllAndClearOptions]="true"
        appendTo="body" dropDownPosition="auto"></app-select-menu>
    </div>

    <div class="match-terms-container">
      <div class="match-terms-title">Brand Aliases (for AI detection)
        <app-tooltip-icon [text]="tooltips.aliases" [wide]="true"></app-tooltip-icon>
      </div>
      <app-form-chip-list [chips]="selectedAliases" [control]="aliases"></app-form-chip-list>
      <div class="add-domain-container">
        <app-titled-input [control]="manualAlias">
          <ng-content>
            <input type="text" class="add-domain-input" (keydown.enter)="$event.preventDefault(); addManualAlias()" [formControl]="manualAlias"
              placeholder="Type an alias name to add it (e.g. BrewDog USA)">
          </ng-content>
        </app-titled-input>
        <app-button text="Add alias" [class]="manualAlias.invalid ? 'secondary disabled' : 'secondary'"
          (clicked)="addManualAlias()"></app-button>
      </div>
    </div>

  </div>

  <footer class="drawer-footer">
    <app-button (clicked)="cancel()" class="cancel" text="cancel"></app-button>
    <app-button (clicked)="submit()" [class]="form.invalid || form.pending || saving() ? 'primary disabled' : 'primary'"
      [text]="configs[currentMode].submitText"></app-button>
  </footer>

  <app-loader *ngIf="saving()"></app-loader>
</div>
